/**
 * Dietary restriction ranking based on display importance.
 * Lower index = higher priority in display.
 *
 * Note: User's own dietary restrictions are always shown first (handled separately).
 * This ranking is for non-matching restrictions. Lifestyle/nutrition tags are ranked
 * higher than allergen tags because users with allergies will have them in their
 * profile (so they'll be prioritized anyway), while for other users, lifestyle/nutrition
 * signals are more meaningful than seeing "Nut-Free" or "Egg-Free".
 *
 * Based on VALID_DIETARY_RESTRICTIONS from backend (src/services/recipeService.ts)
 */
const DIETARY_RESTRICTION_RANKING: string[] = [
  // Lifestyle dietary patterns (exclude entire food groups)
  'Vegan',
  'Vegetarian',
  'Pescatarian',

  // Specialized diet programs
  'Whole30',
  'Paleo',
  'Keto',

  // Nutritional focus (important signals for health-conscious users)
  'High-Protein',
  'High-Fiber',
  'Low-Calorie',
  'Low-Carb',
  'Low-Sugar',
  'Low-Fat',
  'Low-Saturated-Fat',
  'Low-Cholesterol',
  'Low-Sodium',

  // Religious/cultural dietary laws
  'Kosher',
  'Halal',

  // Allergen-free (users with allergies have these in their profile, so they'll
  // be prioritized via user-matching; for others, these are less meaningful signals)
  'Gluten-Free',
  'Dairy-Free',
  'Nut-Free',
  'Egg-Free',
  'Soy-Free',

  // Medical dietary restrictions
  'Low-FODMAP',
  'Low-Residue',
];

/**
 * Redundancy rules: if the key is present, remove the values.
 * - Vegan implies Vegetarian, Pescatarian, Dairy-Free, Egg-Free
 * - Vegetarian implies Pescatarian
 */
const REDUNDANT_RESTRICTIONS: Record<string, string[]> = {
  'Vegan': ['Vegetarian', 'Pescatarian', 'Dairy-Free', 'Egg-Free'],
  'Vegetarian': ['Pescatarian'],
};

/**
 * Restrictions that are only shown if the user has them in their profile.
 * These are niche/specific dietary laws that aren't broadly relevant.
 */
const USER_ONLY_RESTRICTIONS: string[] = [
  'Kosher',
  'Halal',
  'Low-FODMAP',
  'Low-Residue',
];

/**
 * Every comparison in this module is case-insensitive on purpose. Stored
 * restrictions come from several writers (AI generation, catalog seeding,
 * external import, the recipe editor, migrations) and casing has drifted
 * before: a normalizer that title-cased values turned "Low-FODMAP" into
 * "Low-Fodmap", which made exact-match comparisons here silently fail — the
 * tag stopped ranking against the viewer's profile and stopped being treated
 * as viewer-only. The backend canonicalizes on write now, but matching
 * case-insensitively keeps display correct for data written before that fix
 * (and for anything a future writer gets wrong).
 */
const eq = (a: string, b: string): boolean =>
  a.toLowerCase() === b.toLowerCase();

const includesIgnoreCase = (list: string[], value: string): boolean =>
  list.some((item) => eq(item, value));

/**
 * Canonical display spelling for a restriction, so a mis-cased stored value
 * still renders correctly ("Low-Fodmap" -> "Low-FODMAP"). Unknown values are
 * returned unchanged.
 */
export const canonicalRestrictionLabel = (restriction: string): string =>
  DIETARY_RESTRICTION_RANKING.find((r) => eq(r, restriction)) ?? restriction;

/**
 * Filters out redundant dietary restrictions.
 * For example, if a recipe is Vegan, we don't need to also show Vegetarian or Dairy-Free.
 */
export const filterRedundantRestrictions = (restrictions: string[]): string[] => {
  const toRemove = new Set<string>();

  for (const restriction of restrictions) {
    const key = Object.keys(REDUNDANT_RESTRICTIONS).find((k) =>
      eq(k, restriction)
    );
    const redundant = key ? REDUNDANT_RESTRICTIONS[key] : undefined;
    if (redundant) {
      redundant.forEach((r) => toRemove.add(r.toLowerCase()));
    }
  }

  return restrictions.filter((r) => !toRemove.has(r.toLowerCase()));
};

/**
 * Filters out "user-only" restrictions (Kosher, Halal, Low-FODMAP,
 * Low-Residue) unless the user has them in their profile. These are niche
 * dietary requirements that aren't relevant to users who don't follow them.
 */
export const filterUserOnlyRestrictions = (
  restrictions: string[],
  userDietaryRestrictions: string[]
): string[] => {
  return restrictions.filter((r) => {
    // Keep if it's not a user-only restriction
    if (!includesIgnoreCase(USER_ONLY_RESTRICTIONS, r)) {
      return true;
    }
    // Keep if user has this restriction in their profile
    return includesIgnoreCase(userDietaryRestrictions, r);
  });
};

/**
 * Sorts dietary restrictions by importance ranking.
 * Restrictions not in the ranking list are placed at the end in their original order.
 */
export const sortDietaryRestrictionsByRanking = (restrictions: string[]): string[] => {
  const rankOf = (restriction: string): number =>
    DIETARY_RESTRICTION_RANKING.findIndex((r) => eq(r, restriction));

  return [...restrictions].sort((a, b) => {
    const indexA = rankOf(a);
    const indexB = rankOf(b);

    // If both are in the ranking, sort by rank
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }

    // If only A is in the ranking, it comes first
    if (indexA !== -1) {
      return -1;
    }

    // If only B is in the ranking, it comes first
    if (indexB !== -1) {
      return 1;
    }

    // If neither is in the ranking, maintain original order
    return 0;
  });
};

